import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Box,
  Grid,
  IconButton,
  useTheme,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import PrintIcon from "@mui/icons-material/Print";

const ViewTestsDialog = ({
  isOpen,
  onClose,
  tests,
  doctorName,
  patientName,
  patientId,
  requestedDate,
  total_amount,
  handlePrint,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const logoUrl = `/daudzai-logo.jpeg`;

  // Format the date into a human-readable format
  const formatDate = (date) => {
    if (!date) return t("laboratory.noData");
    return new Intl.DateTimeFormat("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      weekday: "long",
    }).format(new Date(date));
  };

  return (
    <Dialog open={isOpen} onClose={onClose} maxWidth="md" fullWidth>
      {/* Header Section */}
      <Box
        sx={{
          backgroundColor: theme.palette.primary.main,
          color: "#ffffff",
          padding: "10px 20px",
        }}
      >
        <Grid container alignItems="center" spacing={2}>
          <Grid item xs={2}>
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "80px",
                marginBottom: "10px",
              }}
            >
              <img
                src={logoUrl}
                alt="Hospital Logo"
                style={{
                  maxHeight: "100%",
                  maxWidth: "100%",
                  objectFit: "contain",
                  borderRadius: "5px",
                  boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.1)",
                  backgroundColor: "#fff",
                  padding: "5px",
                }}
              />
            </div>
          </Grid>
          <Grid item xs={8} textAlign="center">
            <Typography variant="h4" fontWeight="bold">
              {t("print.turkishClinic")}
            </Typography>
            <Typography variant="body1">{t("print.turkishClinic")}</Typography>
            <Typography variant="subtitle2">
              {t("print.address")}
            </Typography>
          </Grid>
          <Grid item xs={2} textAlign="right">
            <IconButton onClick={handlePrint} sx={{ color: "#ffffff" }}>
              <PrintIcon />
            </IconButton>
          </Grid>
        </Grid>
      </Box>

      {/* Patient and Test Information */}
      <DialogContent sx={{ padding: "20px", backgroundColor: "#ffffff" }}>
        <Box
          sx={{
            border: "2px solid",
            borderColor: theme.palette.primary.main,
            borderRadius: "10px",
            padding: "15px",
            marginBottom: "20px",
          }}
        >
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Typography variant="subtitle1" fontWeight="bold">
                {t("laboratory.patientName")}:{" "}
                <Typography component="span" fontWeight="400">
                  {patientName || t("laboratory.noData")}
                </Typography>
              </Typography>
              <Typography variant="subtitle1" fontWeight="bold">
                {t("laboratory.patientId")}:{" "}
                <Typography component="span" fontWeight="400">
                  {patientId || t("laboratory.noData")}
                </Typography>
              </Typography>
              <Typography variant="subtitle1" fontWeight="bold">
                {t("laboratory.requestedDate")}:{" "}
                <Typography component="span" fontWeight="400">
                  {formatDate(requestedDate)}
                </Typography>
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="subtitle1" fontWeight="bold">
                {t("laboratory.doctorName")}:{" "}
                <Typography component="span" fontWeight="400">
                  {doctorName || t("laboratory.noData")}
                </Typography>
              </Typography>
            </Grid>
          </Grid>
        </Box>

        {/* Tests Table */}
        {tests.length > 0 ? (
          <TableContainer component={Paper} sx={{ marginBottom: "20px" }}>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: theme.palette.primary.main }}>
                  <TableCell
                    sx={{
                      fontWeight: "bold",
                      color: "#ffffff",
                      textAlign: "center",
                    }}
                  >
                    #
                  </TableCell>
                  <TableCell sx={{ fontWeight: "bold", color: "#ffffff" }}>
                    {t("laboratory.testName")}
                  </TableCell>
                  <TableCell sx={{ fontWeight: "bold", color: "#ffffff" }}>
                    {t("laboratory.resultValue")}
                  </TableCell>
                  <TableCell sx={{ fontWeight: "bold", color: "#ffffff" }}>
                    {t("laboratory.normalRange")} ({t("laboratory.units")})
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: "bold",
                      color: "#ffffff",
                      textAlign: "right",
                    }}
                  >
                    {t("laboratory.costInAfghani")}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody sx={{ bgcolor: "#ccfcfc" }}>
                {tests.map((test, index) => (
                  <TableRow key={index}>
                    <TableCell
                      sx={{
                        padding: "16px",
                        fontWeight: "bold",
                        textAlign: "center",
                      }}
                    >
                      {index + 1}
                    </TableCell>
                    <TableCell sx={{ padding: "16px", fontSize: "15px" }}>
                      {test.test_type_display.name}
                    </TableCell>
                    <TableCell sx={{ padding: "16px", fontSize: "15px" }}>
                      {test.test_type_display.result_value !== undefined
                        ? `${test.test_type_display.result_value} ${test.test_type_display.units}`
                        : t("laboratory.noResultAvailable")}
                    </TableCell>
                    <TableCell sx={{ padding: "16px", fontSize: "15px" }}>
                      {test.test_type_display.reference_range
                        ? test.test_type_display.reference_range
                        : test.test_type_display.min_value !== null &&
                          test.test_type_display.max_value !== null
                        ? `${test.test_type_display.min_value} - ${
                            test.test_type_display.max_value
                          } ${test.test_type_display.units || ""}`
                        : "-"}
                    </TableCell>
                    <TableCell
                      sx={{
                        padding: "16px",
                        fontSize: "15px",
                        textAlign: "right",
                      }}
                    >
                      {test.test_type_display.cost} ؋
                    </TableCell>
                  </TableRow>
                ))}
                {/* Total Price Row */}
                <TableRow>
                  <TableCell colSpan={4} sx={{ fontWeight: "bold" }}>
                    {t("laboratory.totalAmount")}
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: "bold",
                      fontSize: "16px",
                      textAlign: "right",
                    }}
                  >
                    {total_amount} ؋
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "150px",
            }}
          >
            <Typography
              sx={{
                fontSize: "18px",
                color: "#757575",
                fontStyle: "italic",
              }}
            >
              {t("laboratory.noTestsAvailable")}
            </Typography>
          </Box>
        )}
      </DialogContent>

      {/* Footer Section */}
      <Box
        sx={{
          backgroundColor: theme.palette.primary.main,
          color: "#ffffff",
          padding: "20px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Typography variant="subtitle2">
          {t("print.website")} | {t("print.email")}
        </Typography>
        <Typography variant="subtitle2">{t("print.phoneNumber")}</Typography>
      </Box>
    </Dialog>
  );
};

export default ViewTestsDialog;

